import React, { useState, useEffect, useRef } from 'react';
import './FinancialSolutions.css';
import FinancialSolutionsTitle from './FinancialSolutionsTitle';

const FinancialSolutions = () => {
  const [activeTab, setActiveTab] = useState('INSURANCE');
  const [activeCategory, setActiveCategory] = useState('Life Insurance');
  const [isMobile, setIsMobile] = useState(false);
  const cardsScrollRef = useRef(null);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /* Sidebar State */
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('INSURANCE');
  const [sidebarProduct, setSidebarProduct] = useState('');
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleEnquire = (productTitle) => {
    let selectedCategory = productTitle;
    let selectedTab = 'INSURANCE';

    const lifeKeywords = ['Life', 'Term', 'ULIP', 'Savings', 'Retirement', 'ABSLI'];
    const healthKeywords = ['Health', 'Wellness', 'Top-Up', 'Activ'];
    const motorKeywords = ['Motor', 'Bike', 'Car'];
    const paymentKeywords = ['Credit', 'Cards', 'Card'];

    if (lifeKeywords.some(k => productTitle.includes(k))) {
      selectedCategory = 'Life Insurance';
      selectedTab = 'INSURANCE';
    } else if (healthKeywords.some(k => productTitle.includes(k))) {
      selectedCategory = 'Health Insurance';
      selectedTab = 'INSURANCE';
    } else if (paymentKeywords.some(k => productTitle.includes(k))) {
      selectedCategory = 'Credit Cards';
      selectedTab = 'PAYMENTS';
    } else if (motorKeywords.some(k => productTitle.includes(k))) {
      selectedCategory = 'Motor Insurance';
      selectedTab = 'INSURANCE';
    }

    setSidebarTab(selectedTab);
    setSidebarProduct(selectedCategory);
    setIsSidebarOpen(true);
    setSubmitStatus(null);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setSidebarTab('INSURANCE');
    setEnquiryName('');
    setEnquiryPhone('');
    setSubmitStatus(null);
  };

  const handleSidebarSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Using the same Web3Forms key
    const formData = {
      access_key: 'c8a12abf-15ee-4ce0-a237-0a0b22265ae0',
      contact_name: enquiryName,
      contact_phone: enquiryPhone,
      interested_product: sidebarProduct,
      subject: `New Financial Solutions Enquiry: ${sidebarProduct}`,
      from_name: 'InstaCredMoney Website'
    };

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (result.success) {
        setSubmitStatus('success');
        setEnquiryName('');
        setEnquiryPhone('');
        setTimeout(() => {
          closeSidebar();
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const insuranceSidebarProducts = [
    { title: 'Life Insurance', icon: '☂️', type: 'Life Insurance' },
    { title: 'Health Insurance', icon: '⚕️', type: 'Health Insurance' },
    { title: 'Motor Insurance', icon: '🚗', type: 'Motor Insurance' }
  ];

  const paymentsSidebarProducts = [
    { title: 'Credit Cards', icon: '💳', type: 'Credit Cards' }
  ];

  const loansSidebarProducts = [
    { title: 'Housing Finance', icon: '🏠', type: 'Home Loan' },
    { title: 'Personal Finance', icon: '🧍', type: 'Personal Loan' },
    { title: 'SME & Business', icon: '🏢', type: 'Business Loan' },
    { title: 'Loan Against Property', icon: '🏡', type: 'Loan Against Property' }
  ];

  const getSidebarAlertText = () => {
    if (sidebarProduct.includes('Life') || sidebarProduct.includes('Term') || sidebarProduct.includes('Savings') || sidebarProduct.includes('Retirement') || sidebarProduct.includes('ULIP')) return '98.12% Claim Settlement Ratio for FY 2022-23';
    if (sidebarProduct.includes('Health') || sidebarProduct.includes('Top-Up')) return 'Earn up to 100% premium as HealthReturns';
    if (sidebarProduct.includes('Motor') || sidebarProduct.includes('Bike') || sidebarProduct.includes('Car')) return 'Range of add-ons for coverage enhancement';
    if (sidebarProduct.includes('Credit') || sidebarProduct.includes('Card')) return 'Maximize your lifestyle with exclusive rewards and privileges';
    return 'Detailed plans tailored for your needs';
  };

  const getSidebarProducts = () => {
    if (sidebarTab === 'PAYMENTS') return paymentsSidebarProducts;
    if (sidebarTab === 'LOANS') return loansSidebarProducts;
    return insuranceSidebarProducts;
  };

  const loanCategories = [
    { title: 'Housing Finance', description: 'Your dream home meets our financial expertise' },
    { title: 'Personal Finance', description: 'Quick and easy personal finance solutions' },
    { title: 'SME & Business Finance', description: 'Empower your business growth with our tailored financial solutions' },
    { title: 'Loan Against Property', description: 'Unlock the hidden value of your property for your financial needs' }
  ];

  const insuranceCategories = [
    { title: 'Life Insurance', description: 'Bring security and peace to life\'s unpredictability' },
    { title: 'Health Insurance', description: 'Guard your well-being with comprehensive health insurance coverage' },
    { title: 'Motor Insurance', description: 'Drive worry-free with comprehensive Motor Insurance solutions.' }
  ];

  const paymentsCategories = [
    { title: 'Cards', description: 'Enjoy exclusive benefits and rewards with our premium cards' }
  ];

  const loanData = {
    'Housing Finance': {
      products: [
        {
          id: 1,
          title: 'Home Loan',
          image: 'home-loan',
          color: '#f5a623',
          description: 'Fulfill your dream of owning a home with attractive interest rates.'
        },
        {
          id: 2,
          title: 'Balance Transfer',
          image: 'balance-transfer',
          color: '#8bc34a',
          description: 'Transfer your existing loan for better rates and savings.'
        },
        {
          id: 3,
          title: 'Top-Up Home Loan',
          image: 'top-up',
          color: '#f5a623',
          description: 'Get extra funds on your existing home loan for personal needs.'
        },
        {
          id: 4,
          title: 'Loan Against Property',
          image: 'loan-property',
          color: '#8bc34a',
          description: 'Unlock the value of your property for your financial needs.'
        }
      ],
      description: 'Your dream home meets our financial expertise',
      alertText: 'Home loans, starting from just 9.05% p.a.',
      popular: [
        {
          category: 'HOME LOAN',
          title: 'Home Construction Loan',
          icon: 'construction'
        },
        {
          category: 'HOME LOAN',
          title: 'Home Extension Loan',
          icon: 'extension'
        },
        {
          category: 'HOME LOAN',
          title: 'Home Improvement Loan',
          icon: 'improvement'
        },
        {
          category: 'HOME LOAN',
          title: 'Plot and Home Construction Loan',
          icon: 'plot'
        }
      ]
    },
    'Personal Finance': {
      products: [
        {
          id: 1,
          title: 'Personal Loan',
          image: 'personal-loan',
          color: '#cddc39',
          description: 'Meet all your dreams and needs with a collateral-free Personal Loan'
        },
        {
          id: 2,
          title: 'Flexi Loan',
          image: 'flexi-loan',
          color: '#fff176',
          description: 'Make loan repayment flexible and hassle-free with Flexi Loans'
        },
        {
          id: 3,
          title: 'Instant Personal Loan',
          image: 'instant-personal-loan',
          color: '#ffca28',
          description: 'Get quick funds with We Care Finserv Instant Personal Loan'
        }
      ],
      description: 'Quick and easy personal finance solutions',
      alertText: 'Instant approval and quick disbursal for your personal needs',
      popular: []
    },
    'SME & Business Finance': {
      products: [
        {
          id: 1,
          title: 'Line Of Credit',
          image: 'line-of-credit',
          color: '#cddc39',
          description: 'Meet your working capital needs with a flexible Line of Credit.'
        },
        {
          id: 2,
          title: 'Secured Business Loan',
          image: 'secured-business-loan',
          color: '#ffca28',
          description: 'Grow your business with high-value secured loans at low rates.'
        },
        {
          id: 3,
          title: 'Supply Chain Financing',
          image: 'supply-chain',
          color: '#66bb6a', // Greenish from image
          description: 'Optimize your cash flow with efficient supply chain financing solutions.'
        },
        {
          id: 4,
          title: 'Unsecured Business Loan',
          image: 'unsecured-business-loan',
          color: '#cddc39',
          description: 'Get quick funds for your business without collateral.'
        },
        {
          id: 5,
          title: 'Working Capital Demand Loan',
          image: 'working-capital',
          color: '#ffca28',
          description: 'Manage your day-to-day operations with our working capital loans.'
        }
      ],
      description: 'Empower your business growth with our tailored financial solutions',
      alertText: 'Flexible and scalable financing options for your business needs',
      popular: []
    },
    'Loan Against Property': {
      products: [
        {
          id: 1,
          title: 'Commercial Property Purchase',
          image: 'commercial-property',
          color: '#cddc39',
          description: 'Finance your commercial property purchase with ease.'
        },
        {
          id: 2,
          title: 'Construction Finance',
          image: 'construction-finance',
          color: '#ffca28',
          description: 'Get funds for residential or commercial construction projects.'
        },
        {
          id: 3,
          title: 'Lease Rental Discounting',
          image: 'lease-rental',
          color: '#66bb6a',
          description: 'Monetize your future rental income for immediate capital.'
        }
      ],
      description: 'Unlock the hidden value of your property for your financial needs',
      alertText: 'Best value for your property with attractive interest rates',
      popular: []
    }
  };

  const insuranceData = {
    'Life Insurance': {
      products: [
        { id: 1, title: 'Term Plan', image: 'term-plan', imageSrc: '/images/financialsolution/insurance/termplan.png', color: '#8bc34a', description: 'An affordable way to protect your loved ones\' future' },
        { id: 2, title: 'ULIP', image: 'ulip', imageSrc: '/images/financialsolution/insurance/ulip.jpg', color: '#f5a623', description: 'Dual benefit of investment and insurance for long-term goals.' },
        { id: 3, title: 'Savings Plan', image: 'savings-plan', imageSrc: '/images/financialsolution/insurance/savingplan.png', color: '#8bc34a', description: 'Secure your family\'s future while building a corpus.' },
        { id: 4, title: 'Retirement Plan', image: 'retirement-plan', imageSrc: '/images/financialsolution/insurance/retirementplan.jpg', color: '#f5a623', description: 'Plan for a financially independent and stress-free retirement.' }
      ],
      popular: [
        { category: 'TERM PLAN', title: 'ABSLI DigiShield Plan', icon: 'shield' },
        { category: 'SAVINGS PLAN', title: 'ABSLI Nishchit Aayush Plan', icon: 'savings' },
        { category: 'RETIREMENT PLAN', title: 'ABSLI Guaranteed Annuity Plan', icon: 'retirement' },
        { category: 'TERM PLAN', title: 'ABSLI Salaried Term Plan', icon: 'umbrella' }
      ],
      alertText: '98.12% Claim Settlement Ratio for FY 2022-23'
    },
    'Health Insurance': {
      products: [
        { id: 1, title: 'Health & Wellness Plan', image: 'health-wellness', imageSrc: '/images/financialsolution/insurance/healthplan.png', color: '#8bc34a', description: 'Comprehensive coverage for hospitalization and wellness benefits.' },
        { id: 2, title: 'Top-Up Plans', image: 'top-up-plans', imageSrc: '/images/financialsolution/insurance/topupplan.jpg', color: '#f5a623', description: 'Enhance your health cover at an affordable premium.' }
      ],
      popular: [
        { category: 'HEALTH & WELLNESS', title: 'Activ Fit Plus', icon: 'health' },
        { category: 'HEALTH & WELLNESS', title: 'Activ Assure Diamond', icon: 'heart' }
      ],
      alertText: 'Earn up to 100% premium as HealthReturns'
    },
    'Motor Insurance': {
      products: [
        { id: 1, title: 'Bike Insurance', image: 'bike-insurance', imageSrc: '/images/financialsolution/insurance/bikeinsurance.png', color: '#f5a623', description: 'Protect your two-wheeler against accidents and theft.' },
        { id: 2, title: 'Car Insurance', image: 'car-insurance', imageSrc: '/images/financialsolution/insurance/carinsurance.png', color: '#8bc34a', description: 'Complete protection for your car against damages and third-party liabilities.' }
      ],
      popular: [],
      alertText: 'Range of add-ons for coverage enhancement'
    }
  };

  const popularProducts = [
    {
      category: 'HOME LOAN',
      title: 'Home Construction Loan',
      icon: 'construction'
    },
    {
      category: 'HOME LOAN',
      title: 'Home Extension Loan',
      icon: 'extension'
    },
    {
      category: 'HOME LOAN',
      title: 'Home Improvement Loan',
      icon: 'improvement'
    },
    {
      category: 'HOME LOAN',
      title: 'Plot and Home Construction Loan',
      icon: 'plot'
    }
  ];

  const paymentsProducts = [
    {
      id: 1,
      title: 'Credit Cards',
      image: 'credit-cards',
      imageSrc: '/images/financialsolution/payments/creditcard.png',
      color: '#f5a623',
      description: 'Maximize your lifestyle with exclusive rewards and privileges.'
    }
  ];

  const paymentsPopularProducts = [];

  const categories = activeTab === 'PAYMENTS' ? paymentsCategories : (activeTab === 'INSURANCE' ? insuranceCategories : loanCategories);

  const getProducts = () => {
    if (activeTab === 'PAYMENTS') return paymentsProducts;
    if (activeTab === 'INSURANCE') return insuranceData[activeCategory]?.products || [];
    if (activeTab === 'LOANS') return loanData[activeCategory]?.products || [];
    return [];
  };

  const getPopular = () => {
    if (activeTab === 'PAYMENTS') return paymentsPopularProducts;
    if (activeTab === 'INSURANCE') return insuranceData[activeCategory]?.popular || [];
    if (activeTab === 'LOANS') return loanData[activeCategory]?.popular || [];
    return [];
  };

  const getAlertText = () => {
    if (activeTab === 'PAYMENTS') return 'Unlock a world of rewards and benefits with our credit cards';
    if (activeTab === 'INSURANCE') return insuranceData[activeCategory]?.alertText || '';
    if (activeTab === 'LOANS') return loanData[activeCategory]?.alertText || 'Financial solutions tailored for you';
    return '';
  };

  const handleCategoryClick = (title) => {
    if (activeTab === 'INSURANCE' || activeTab === 'LOANS') {
      setActiveCategory(title);
    }
  };

  const products = getProducts();
  const popular = getPopular();
  const alertText = getAlertText();

  // Auto-scroll functionality for product cards on mobile
  useEffect(() => {
    if (!isMobile || !cardsScrollRef.current || products.length === 0) return;

    const container = cardsScrollRef.current;
    const cardWidth = container.firstChild?.offsetWidth || 0;
    const gap = 12;
    const scrollStep = cardWidth + gap;
    const totalCards = products.length;
    let currentIndex = 0;

    const autoScroll = setInterval(() => {
      currentIndex = (currentIndex + 1) % totalCards;
      const scrollPosition = currentIndex * scrollStep;

      if (scrollPosition >= container.scrollWidth - container.clientWidth) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
        currentIndex = 0;
      } else {
        container.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }, 3000);

    return () => clearInterval(autoScroll);
  }, [isMobile, products.length, activeCategory, activeTab]);

  return (
    <section className="financial-solutions" id="financial-solutions">
      <div className="solutions-container">
        <FinancialSolutionsTitle />

        <div className="tabs-container">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'LOANS' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('LOANS');
                setActiveCategory('Housing Finance');
              }}
            >
              LOANS
            </button>
            <button
              className={`tab ${activeTab === 'INSURANCE' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('INSURANCE');
                setActiveCategory('Life Insurance');
              }}
            >
              INSURANCE
            </button>
            <button
              className={`tab ${activeTab === 'PAYMENTS' ? 'active' : ''}`}
              onClick={() => setActiveTab('PAYMENTS')}
            >
              PAYMENTS
            </button>
          </div>
        </div>

        <div className="content-section">
          <div className="sidebar-categories">
            {categories.map((category, index) => (
              <div
                key={index}
                className={`category-item ${activeCategory === category.title ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category.title)}
              >
                <h3 className="category-title">{category.title}</h3>
                {activeCategory === category.title ? (
                  <>
                    {category.description && <p className="category-description">{category.description}</p>}
                    <div className="category-actions">
                      <button className="btn-enquire" onClick={() => handleEnquire(category.title)}>ENQUIRE NOW</button>
                    </div>
                  </>
                ) : null}
              </div>
            ))}
          </div>

          <div className="main-content">
            {/* Mobile Category Info - Shows active category details on mobile */}
            <div className="mobile-category-info">
              <h3 className="mobile-category-title">{activeCategory}</h3>
              <p className="mobile-category-desc">
                {categories.find(c => c.title === activeCategory)?.description || ''}
              </p>
              <button className="btn-enquire" onClick={() => handleEnquire(activeCategory)}>ENQUIRE NOW</button>
            </div>

            <div className="banner-alert">
              <span className="alert-icon">💡</span>
              <span className="alert-text">{alertText}</span>
            </div>

            <div className="loan-cards-grid" ref={cardsScrollRef}>
              {products.map((product) => (
                <div
                  key={product.id}
                  className="loan-card"
                  onClick={() => handleEnquire(product.title)}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className={`card-image ${product.image}`}
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: product.imageSrc ? '#fff' : undefined
                    }}
                  >
                    {product.imageSrc && (
                      <img
                        src={process.env.PUBLIC_URL + product.imageSrc}
                        alt={product.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="card-footer" style={{ backgroundColor: product.color }}>
                    <span className="card-title">{product.title}</span>
                  </div>
                  <div className="card-hover-content" style={{ backgroundColor: product.color }}>
                    <h4 className="hover-title">{product.title}</h4>
                    <p className="hover-description">{product.description}</p>
                    <button className="hover-btn" onClick={() => handleEnquire(product.title)}>ENQUIRE NOW</button>
                  </div>
                </div>
              ))}
            </div>

            {popular.length > 0 && (
              <div className="popular-products">
                <h3 className="popular-title">Popular Products</h3>
                <div className="products-grid">
                  {popular.map((product, index) => (
                    <div key={index} className="product-item" onClick={() => handleEnquire(product.title)}>
                      <div className={`product-icon ${product.icon}`}></div>
                      <div className="product-info">
                        <span className="product-category">{product.category}</span>
                        <span className="product-title">{product.title}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar Form Overlay */}
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}>
          <div className="sidebar-container" onClick={(e) => e.stopPropagation()}>

            <button className="sidebar-close-btn" onClick={closeSidebar}>×</button>
            <div className="sidebar-content">
              <h2 className="sidebar-title">Start Your <span className="highlight-red">Journey</span></h2>
              <p className="sidebar-subtitle">Your Path To Endless Financial Continuity From A Brand You Trust</p>

              <div className="sidebar-tabs">
                <span
                  className={`sidebar-tab ${sidebarTab === 'LOANS' ? 'active' : ''}`}
                  onClick={() => setSidebarTab('LOANS')}
                  style={{ cursor: 'pointer' }}
                >
                  LOANS
                </span>
                <span
                  className={`sidebar-tab ${sidebarTab === 'INSURANCE' ? 'active' : ''}`}
                  onClick={() => setSidebarTab('INSURANCE')}
                  style={{ cursor: 'pointer' }}
                >
                  INSURANCE
                </span>
                <span
                  className={`sidebar-tab ${sidebarTab === 'PAYMENTS' ? 'active' : ''}`}
                  onClick={() => setSidebarTab('PAYMENTS')}
                  style={{ cursor: 'pointer' }}
                >
                  PAYMENTS
                </span>
              </div>

              <div className="sidebar-products-grid">
                {getSidebarProducts().map((p, idx) => (
                  <div
                    key={idx}
                    className={`sidebar-product-card ${sidebarProduct.includes(p.type.split(' ')[0]) ? 'selected' : ''}`}
                    onClick={() => setSidebarProduct(p.title)}
                  >
                    <div className="sidebar-product-icon">{p.icon}</div>
                    <span className="sidebar-product-name">{p.title}</span>
                    {sidebarProduct.includes(p.type.split(' ')[0]) && <div className="check-badge">✓</div>}
                  </div>
                ))}
              </div>

              <div className="sidebar-alert-box">
                {getSidebarAlertText()}
              </div>

              <form className="sidebar-form" onSubmit={handleSidebarSubmit}>
                <div className="form-field">
                  <label>Full Name</label>
                  <input
                    type="text"
                    placeholder="Enter Your Name"
                    value={enquiryName}
                    onChange={(e) => setEnquiryName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Contact Number</label>
                  <div className="phone-prefix-input">
                    <span className="prefix">91 |</span>
                    <input
                      type="tel"
                      placeholder="Enter Your Phone Number"
                      value={enquiryPhone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= 10) setEnquiryPhone(val);
                      }}
                      required
                    />
                  </div>
                </div>

                {submitStatus === 'error' && <p className="error-text">Failed to submit. Please try again.</p>}

                <button type="submit" className="sidebar-submit-btn" disabled={isSubmitting || !enquiryName || enquiryPhone.length !== 10}>
                  {isSubmitting ? 'SENDING...' : 'ENQUIRE NOW'}
                </button>
              </form>
            </div>
            {submitStatus === 'success' && (
              <div className="success-modal-overlay">
                <div className="success-content">
                  <div className="success-icon-wrapper">
                    <div className="checkmark"></div>
                  </div>
                  <h4 className="success-title">Enquiry Sent!</h4>
                  <p className="success-message-text">We have received your details and will get back to you shortly.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default FinancialSolutions;
